import React from "react";
import AnimeEye from "./AnimeEye";
import { PlainEye, PlainMouth } from "./PlainEye";
import { ANIME_EXPR, PLAIN_EXPR, RUNE_SETS, AI_EXPR, octagonPath, sparklePath } from "./expressions";
import { MANGA_EXPR } from "./manga-paths";

// ── SaxaFrame props ──
interface SaxaFrameProps {
  size: number;
  expression: string;
  mode: string;
  showBrackets: boolean;
  dark: boolean;
}

// ── PulsingFrame props ──
interface PulsingFrameProps {
  children: React.ReactNode;
  animate: boolean;
}

const SaxaFrame: React.FC<SaxaFrameProps> = ({
  size,
  expression,
  mode,
  showBrackets,
  dark,
}) => {
  const s = size;
  const cx = s / 2;
  const cy = s / 2;
  const outerR = s * 0.44;
  const innerR = outerR * Math.cos(Math.PI / 8) * 0.96;
  const outerStroke = s * 0.012;

  const frameColor = dark ? "#60A5FA" : "#111111";
  const featureColor = dark ? "#1E3A8A" : "#1a1a2e";
  const irisAccent = dark ? "#3B82F6" : "#4a4a6a";
  const bgColor = dark ? "#0B1120" : "#F8FAFC";

  const framePath =
    octagonPath(cx, cy, outerR, 0) + " " + octagonPath(cx, cy, innerR, 22.5);
  const t = "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)";
  const isIdle = expression === "idle";
  const isAi = mode === "ai";

  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
      <defs>
        <filter id={`glow-${s}`}>
          <feGaussianBlur stdDeviation={s * 0.004} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <path
        d={framePath}
        fill={frameColor}
        fillRule="evenodd"
        filter={dark ? `url(#glow-${s})` : undefined}
      />

      {/* < > brackets — shown for runes mode OR idle (any mode except AI) */}
      {((showBrackets && mode === "runes") || (isIdle && !isAi)) && (
        <g
          opacity={dark ? 0.8 : 0.18}
          stroke={dark ? "#60A5FA" : frameColor}
          strokeWidth={outerStroke * 0.7}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline
            points={`${cx - innerR * 0.72},${cy - s * 0.05} ${cx - innerR * 0.88},${cy} ${cx - innerR * 0.72},${cy + s * 0.05}`}
          />
          <polyline
            points={`${cx + innerR * 0.72},${cy - s * 0.05} ${cx + innerR * 0.88},${cy} ${cx + innerR * 0.72},${cy + s * 0.05}`}
          />
        </g>
      )}

      {/* Idle glyphs — same runes dots for all modes except AI */}
      {isIdle && !isAi && (
        <g>
          {RUNE_SETS.idle.glyphs.map((g: string, i: number) => (
            <text
              key={i}
              x={cx + (i - 2) * s * 0.1}
              y={cy + s * 0.03}
              textAnchor="middle"
              dominantBaseline="central"
              fill={dark ? "#60A5FA" : featureColor}
              fontSize={s * 0.13}
              fontFamily="serif"
              opacity={dark ? 0.8 : 0.6}
              style={{
                transition: "all 0.4s ease",
                transitionDelay: `${i * 60}ms`,
              }}
            >
              {g}
            </text>
          ))}
        </g>
      )}

      {/* Anime eyes — not when idle */}
      {mode === "anime" && !isIdle &&
        (() => {
          const expr = ANIME_EXPR[expression] || ANIME_EXPR.neutral;
          const sc = s / 115;
          const palette = dark ? expr.colors.dark : expr.colors.light;
          const browColor = dark ? palette.sclera : palette.iris;
          const browOpacity = dark ? 0.7 : 0.75;
          return (
            <g transform={`translate(${cx}, ${cy}) scale(${sc})`}>
              {/* Brows */}
              <path
                d={expr.brow.left.d}
                fill="none"
                stroke={browColor}
                strokeWidth={expr.brow.left.w}
                strokeLinecap="round"
                opacity={browOpacity}
                style={{ transition: t }}
              />
              <path
                d={expr.brow.right.d}
                fill="none"
                stroke={browColor}
                strokeWidth={expr.brow.right.w}
                strokeLinecap="round"
                opacity={browOpacity}
                style={{ transition: t }}
              />
              {/* Eyes */}
              <AnimeEye
                e={expr.left}
                color={palette.iris}
                irisColor={palette.accent}
                bgColor={bgColor}
                scleraColor={palette.sclera}
                flip={false}
                clipId={`el${s}`}
              />
              <AnimeEye
                e={expr.right}
                color={palette.iris}
                irisColor={palette.accent}
                bgColor={bgColor}
                scleraColor={palette.sclera}
                flip={true}
                clipId={`er${s}`}
              />
            </g>
          );
        })()}

      {/* Plain emoji — not when idle */}
      {mode === "plain" && !isIdle &&
        (() => {
          const expr = PLAIN_EXPR[expression] || PLAIN_EXPR.neutral;
          return (
            <g transform={`translate(${cx}, ${cy}) scale(${s / 115})`}>
              <PlainEye
                eye={expr.leftEye}
                color={featureColor}
                bgColor={bgColor}
                t={t}
                clipId={`pl${s}`}
              />
              <PlainEye
                eye={expr.rightEye}
                color={featureColor}
                bgColor={bgColor}
                t={t}
                clipId={`pr${s}`}
              />
              <PlainMouth mouth={expr.mouth} color={featureColor} t={t} />
            </g>
          );
        })()}

      {/* Runes — not when idle (idle glyphs rendered above) */}
      {mode === "runes" && !isIdle && (
        <g>
          {(RUNE_SETS[expression] || RUNE_SETS.idle).glyphs.map(
            (g: string, i: number) => (
              <text
                key={i}
                x={cx + (i - 2) * s * 0.1}
                y={cy + s * 0.03}
                textAnchor="middle"
                dominantBaseline="central"
                fill={dark ? "#60A5FA" : featureColor}
                fontSize={s * 0.13}
                fontFamily="serif"
                opacity={dark ? 0.8 : 0.6}
                style={{
                  transition: "all 0.4s ease",
                  transitionDelay: `${i * 60}ms`,
                }}
              >
                {g}
              </text>
            )
          )}
        </g>
      )}

      {/* Manga — not when idle */}
      {mode === "manga" && !isIdle &&
        (() => {
          const expr = MANGA_EXPR[expression] || MANGA_EXPR.neutral;
          const [, , vw, vh] = expr.viewBox.split(" ").map(Number);
          const aspect = vh / vw;
          const fitW = innerR * 1.75;
          const fitH = fitW * aspect;
          return (
            <svg
              x={cx - fitW / 2}
              y={cy - fitH / 2}
              width={fitW}
              height={fitH}
              viewBox={expr.viewBox}
            >
              {expr.paths.map((d: string, i: number) => (
                <path
                  key={i}
                  d={d}
                  fill={featureColor}
                  style={{ transition: "all 0.5s ease" }}
                />
              ))}
            </svg>
          );
        })()}

      {/* AI Core — renders for all expressions including idle */}
      {isAi && (() => {
        const expr = AI_EXPR[expression as keyof typeof AI_EXPR] || AI_EXPR.idle;
        const sc = Math.max(s / 200, 0.22);
        const color = dark ? "#60A5FA" : "#111111";
        const accent = dark ? "#93C5FD" : "#3B82F6";
        const coreR = innerR * expr.coreScale;

        return (
          <g>
            {/* Ambient glow */}
            {dark && (
              <circle cx={cx} cy={cy} r={coreR * 3.5} fill="#60A5FA" opacity={0.05}>
                <animate attributeName="opacity" values="0.03;0.09;0.03"
                  dur={`${expr.pulse}s`} repeatCount="indefinite" />
              </circle>
            )}

            {/* Concentric octagonal rings */}
            {expr.rings.map((ring, i) => {
              const rr = innerR * ring.rScale;
              const sw = Math.max(ring.w * sc, 0.4);
              const spinning = ring.dur !== 0;
              const dir = ring.dur > 0 ? 360 : -360;
              const durSec = Math.abs(ring.dur);

              return (
                <g key={`ring-${i}`} transform={`translate(${cx},${cy})`}>
                  <g>
                    {spinning && (
                      <animateTransform attributeName="transform" type="rotate"
                        from="0 0 0" to={`${dir} 0 0`}
                        dur={`${durSec}s`} repeatCount="indefinite" />
                    )}

                    <path d={octagonPath(0, 0, rr, ring.rot)}
                      fill="none" stroke={color} strokeWidth={sw}
                      opacity={ring.opacity} strokeLinejoin="round" />

                    {expr.showStreams && i > 0 && (
                      <path d={octagonPath(0, 0, rr, ring.rot)}
                        fill="none" stroke={accent}
                        strokeWidth={Math.max(sw * 0.6, 0.3)}
                        strokeDasharray={`${rr * 0.22} ${rr * 0.58}`}
                        opacity={ring.opacity * 0.65}
                        strokeLinejoin="round">
                        <animate attributeName="stroke-dashoffset"
                          from="0" to={`${(expr.streamIn ? 1 : -1) * rr * 2.2}`}
                          dur={`${2.5 + i * 0.6}s`} repeatCount="indefinite" />
                      </path>
                    )}
                  </g>
                </g>
              );
            })}

            {/* Soft arcs between rings */}
            {expr.rayCount > 0 && expr.rings.length > 1 && (() => {
              const pairs: React.ReactNode[] = [];
              for (let ri = 0; ri < expr.rings.length - 1; ri++) {
                const r1 = innerR * expr.rings[ri].rScale;
                const r2 = innerR * expr.rings[ri + 1].rScale;
                const count = Math.min(expr.rayCount, 6);
                const offset = ri * (Math.PI / count);
                for (let i = 0; i < count; i++) {
                  const a1 = (Math.PI * 2 * i / count) + offset - Math.PI / 2;
                  const a2 = a1 + Math.PI / (count * 1.2);
                  const isActive = !expr.activeRays || expr.activeRays.includes(i);
                  pairs.push(
                    <path key={`arc-${ri}-${i}`}
                      d={`M ${cx + Math.cos(a1) * r1},${cy + Math.sin(a1) * r1} Q ${cx + Math.cos((a1 + a2) / 2) * (r1 + r2) * 0.48},${cy + Math.sin((a1 + a2) / 2) * (r1 + r2) * 0.48} ${cx + Math.cos(a2) * r2},${cy + Math.sin(a2) * r2}`}
                      fill="none" stroke={isActive ? accent : color}
                      strokeWidth={Math.max(0.5 * sc, 0.25)}
                      opacity={isActive ? 0.25 : 0.06}
                      strokeLinecap="round"
                    />
                  );
                }
              }
              return pairs;
            })()}

            {/* Orbital particles */}
            {expr.showNodes && expr.rings.map((ring, ri) => {
              const rr = innerR * ring.rScale;
              const count = ri === 0 ? 3 : 4 + ri;
              return Array.from({ length: count }, (_, i) => {
                const nr = Math.max(1.4 * sc, 0.45);
                const dur = 6 + ri * 3 + i * 0.8;
                const dir = ri % 2 === 0 ? 1 : -1;
                const isActive = !expr.activeRays || expr.activeRays.includes(i % expr.rayCount);

                return (
                  <circle key={`orb-${ri}-${i}`}
                    cx={0} cy={0} r={nr}
                    fill={isActive ? accent : color}
                    opacity={0.5}>
                    <animateMotion
                      path={octagonPath(cx, cy, rr, ring.rot)}
                      dur={`${dur}s`}
                      begin={`${(dur / count) * i * dir}s`}
                      repeatCount="indefinite"
                      rotate="auto" />
                    <animate attributeName="opacity"
                      values={isActive ? "0.25;0.85;0.25" : "0.1;0.35;0.1"}
                      dur={`${expr.pulse}s`}
                      begin={`${ri * 0.3 + i * 0.15}s`}
                      repeatCount="indefinite" />
                  </circle>
                );
              });
            })}

            {/* Central Spark */}
            <g transform={`translate(${cx},${cy})`}>
              <defs>
                <radialGradient id={`spark-grad-${s}`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor={dark ? "#C084FC" : "#FFFFFF"} stopOpacity={1}>
                    <animate attributeName="stopColor"
                      values={dark ? "#C084FC;#E9D5FF;#C084FC" : "#FFFFFF;#F5F3FF;#FFFFFF"}
                      dur={`${expr.pulse}s`} repeatCount="indefinite" />
                  </stop>
                  <stop offset="35%" stopColor={dark ? "#7C3AED" : "#A78BFA"} stopOpacity={dark ? 0.9 : 0.95}>
                    <animate attributeName="stopColor"
                      values={dark ? "#7C3AED;#A78BFA;#7C3AED" : "#A78BFA;#C4B5FD;#A78BFA"}
                      dur={`${expr.pulse}s`} repeatCount="indefinite" />
                  </stop>
                  <stop offset="70%" stopColor={dark ? "#6D28D9" : "#6D28D9"} stopOpacity={dark ? 0.6 : 0.9}>
                    <animate attributeName="stopColor"
                      values={dark ? "#6D28D9;#7C3AED;#6D28D9" : "#6D28D9;#7C3AED;#6D28D9"}
                      dur={`${expr.pulse}s`} repeatCount="indefinite" />
                  </stop>
                  <stop offset="100%" stopColor={dark ? "#4C1D95" : "#3B0764"} stopOpacity={dark ? 0.3 : 0.85}>
                    <animate attributeName="stopOpacity"
                      values={dark ? "0.3;0.6;0.3" : "0.7;0.95;0.7"}
                      dur={`${expr.pulse}s`} repeatCount="indefinite" />
                  </stop>
                </radialGradient>
              </defs>

              {/* Halo */}
              <circle cx={0} cy={0} r={coreR * 2.2}
                fill={dark ? "#7C3AED" : "#6D28D9"} opacity={dark ? 0.1 : 0.08}>
                <animate attributeName="opacity"
                  values={dark ? "0.06;0.18;0.06" : "0.04;0.16;0.04"}
                  dur={`${expr.pulse}s`} repeatCount="indefinite" />
                <animate attributeName="r"
                  values={`${coreR * 1.8};${innerR * 0.55};${coreR * 1.8}`}
                  dur={`${expr.pulse}s`} repeatCount="indefinite" />
              </circle>

              {/* Pulsating star — coreR → 80% of innerR */}
              <g>
                <animateTransform attributeName="transform" type="scale"
                  values={`1;${(innerR * 0.8) / coreR};1`} dur={`${expr.pulse}s`}
                  repeatCount="indefinite" />
                <path d={sparklePath(0, 0, coreR)} fill={`url(#spark-grad-${s})`} opacity={0.95} />
              </g>

              {/* Core point */}
              <circle cx={0} cy={0} r={coreR * 0.25}
                fill={dark ? "#E9D5FF" : "#FFFFFF"} opacity={0.9}>
                <animate attributeName="opacity" values="0.7;1;0.7"
                  dur={`${expr.pulse}s`} repeatCount="indefinite" />
              </circle>
            </g>
          </g>
        );
      })()}
    </svg>
  );
};

const PulsingFrame: React.FC<PulsingFrameProps> = ({ children, animate }) => {
  if (!animate) return <>{children}</>;
  return (
    <div style={{ animation: "saxaPulse 3s ease-in-out infinite" }}>
      {children}
      <style>{`@keyframes saxaPulse { 0%,100%{filter:drop-shadow(0 0 0 transparent)} 50%{filter:drop-shadow(0 0 8px rgba(96,165,250,.2))} }`}</style>
    </div>
  );
};

export { SaxaFrame, PulsingFrame };
